import React, { useState } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Heart, Check } from 'lucide-react';
import { createDonation } from '../../../services/stripe';

// Initialize Stripe outside component to avoid recreating
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

// Preset donation amounts in cents
const PRESET_AMOUNTS = [
  { value: 500, label: '$5' },
  { value: 1000, label: '$10' },
  { value: 2500, label: '$25' },
  { value: 5000, label: '$50' },
];

const DonationForm = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [selectedAmount, setSelectedAmount] = useState(1000); // Default $10
  const [customAmount, setCustomAmount] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Determine final amount (use custom if set, otherwise selected preset)
      const finalAmount = customAmount ? Math.round(parseFloat(customAmount) * 100) : selectedAmount;

      // Validate amount
      if (!finalAmount || finalAmount < 100) {
        throw new Error('Minimum donation is $1');
      }
      if (finalAmount > 1000000) {
        throw new Error('Maximum donation is $10,000');
      }

      // Validate email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email for your receipt');
      }

      // Create payment intent
      const { clientSecret } = await createDonation(finalAmount, email, name);

      // Confirm the payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: name || 'Anonymous',
              email: email,
            },
          },
        }
      );

      if (confirmError) {
        throw confirmError;
      }

      if (paymentIntent.status === 'succeeded') {
        onSuccess(finalAmount / 100); // Convert back to dollars for display
      }
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.message);
      setIsProcessing(false);
    }
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      if (value) {
        setSelectedAmount(null); // Clear preset selection
      }
    }
  };

  const handlePresetClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount(''); // Clear custom amount
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Choose Amount (USD)
        </label>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {PRESET_AMOUNTS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetClick(preset.value)}
              className={`py-3 px-4 rounded-lg border-2 transition-all font-semibold
                ${selectedAmount === preset.value && !customAmount
                  ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                  : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600'
                }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
          <input
            type="text"
            placeholder="Custom amount"
            value={customAmount}
            onChange={handleCustomAmountChange}
            className="w-full pl-8 pr-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                     text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500
                     transition-colors"
          />
        </div>
      </div>

      {/* Email Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Email (for receipt) *
        </label>
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                   text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500
                   transition-colors"
        />
      </div>

      {/* Name Input (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Name (optional)
        </label>
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                   text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500
                   transition-colors"
        />
      </div>

      {/* Card Element */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Card Details
        </label>
        <div className="p-4 bg-gray-800 border-2 border-gray-700 rounded-lg">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#ffffff',
                  '::placeholder': {
                    color: '#aab7c4'
                  },
                  iconColor: '#ffffff',
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a'
                }
              },
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-6 rounded-lg
                 hover:from-emerald-600 hover:to-teal-600 transition-colors
                 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg
                 flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          'Processing...'
        ) : (
          <>
            <Heart className="w-5 h-5" />
            Donate ${customAmount || (selectedAmount / 100).toFixed(2)}
          </>
        )}
      </button>

      <p className="text-center text-gray-400 text-xs">
        Your donation helps keep ContextUI free for everyone
      </p>
    </form>
  );
};

const SuccessMessage = () => {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <Check className="w-8 h-8 text-green-500" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
      <p className="text-gray-300 mb-6">
        Your donation has been received successfully.
      </p>
      <p className="text-gray-400 text-sm mb-6">
        A receipt has been sent to your email. Your support helps us keep ContextUI free and accessible for everyone.
      </p>
      <a
        href="/"
        className="inline-block bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
      >
        Return Home
      </a>
    </div>
  );
};

const Donate = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [donationAmount, setDonationAmount] = useState(0);

  const handleSuccess = (amount) => {
    setDonationAmount(amount);
    setIsSuccess(true);
  };

  return (
    <div className="bg-black min-h-screen text-white pt-20 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-4">
            {isSuccess ? 'Donation Complete' : 'Support ContextUI'}
          </h1>
          {!isSuccess && (
            <p className="text-xl text-gray-400">
              ContextUI is free for everyone. Your donation helps us maintain and improve the platform.
            </p>
          )}
        </div>

        {/* Content */}
        <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
          {isSuccess ? (
            <SuccessMessage amount={donationAmount} />
          ) : (
            <Elements stripe={stripePromise}>
              <DonationForm onSuccess={handleSuccess} />
            </Elements>
          )}
        </div>

        {/* Additional Info */}
        {!isSuccess && (
          <div className="text-center mt-8 text-gray-400">
            <p className="mb-2">
              ContextUI is a free desktop workspace for building custom tools with AI.
            </p>
            <p>
              Your contribution helps us keep it that way.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Donate;
